import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { Search, X, ChevronUp, ChevronDown, BadgeCheck, AlertCircle, Mountain, Package, ExternalLink } from "lucide-react";
import { breederUrl } from "../../lib/vendorLinks";

/**
 * v3.89e — Lineage Atlas LIST view.
 *
 * The Cannabis Genetics Map is a great wow-factor visualization but
 * collapses visually at 60+ strains. For browsing 1K+ records you need
 * a flat, scannable table: that's this component.
 *
 * Features:
 *   • Server-side pagination via `/api/lineage?skip&limit` (already supports
 *     `q`, `classification`, `seed_type`, `has_listing`, `missing_parentage`).
 *   • Client-side sort by name / classification / parent / saves / verification.
 *   • Free-text search box + classification chip filter + "has CannaForge listing"
 *     toggle + "missing parentage" toggle.
 *   • Compact row design — strain name (link), parents, classification,
 *     seed/cycle, originator breeder, verification status, save count.
 *
 * Keeps the existing Strain Archive (sectioned discovery) AND the Map
 * untouched — this is a third complementary view.
 */
const PAGE_SIZES = [25, 50, 100];

const CLASS_CHIPS = [
  { id: "",                  label: "All" },
  { id: "indica,indica_dominant", label: "Indica" },
  { id: "sativa,sativa_dominant", label: "Sativa" },
  { id: "hybrid,balanced",   label: "Hybrid" },
];

const VERIFICATION_GLYPH = {
  verified:           { icon: BadgeCheck,   color: "var(--primary-text)", label: "Verified" },
  breeder_submitted:  { icon: Package,      color: "var(--gold)",         label: "Breeder-submitted" },
  community_submitted:{ icon: Package,      color: "var(--text-2)",       label: "Community-submitted" },
  disputed:           { icon: AlertCircle,  color: "var(--secondary)",    label: "Disputed" },
};

export default function LineageList() {
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState(""); // empty = all
  const [seedFilter, setSeedFilter] = useState("");   // "" | "feminized" | "regular" | "autoflower"
  const [hasListingOnly, setHasListingOnly] = useState(false);
  const [missingParentageOnly, setMissingParentageOnly] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Client-side sort
  const [sortBy, setSortBy] = useState("name");  // name | classification | parent_one | save_count | verification_status
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  // Reset to page 0 whenever filters change so the user always starts at
  // page 1 of the filtered result set (not orphaned on page 9 of 3 results).
  useEffect(() => { setPage(0); }, [q, classFilter, seedFilter, hasListingOnly, missingParentageOnly, pageSize]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = {
      limit: pageSize,
      skip: page * pageSize,
    };
    if (q.trim()) params.q = q.trim();
    if (classFilter) params.classification = classFilter;
    if (seedFilter) params.seed_type = seedFilter;
    if (hasListingOnly) params.has_listing = true;
    if (missingParentageOnly) params.missing_parentage = true;
    api.get("/lineage", { params })
      .then((r) => {
        if (!alive) return;
        setDocs(r.data || []);
        // The endpoint doesn't return a total; we approximate from the page
        // size — last page is detected when the response has < pageSize rows.
        // For an exact total, see the `/lineage/archive-sections` endpoint
        // which reports `total_public_records`.
      })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q, classFilter, seedFilter, hasListingOnly, missingParentageOnly, page, pageSize]);

  // Total public records (for header). Cached on mount.
  useEffect(() => {
    let alive = true;
    api.get("/lineage/archive-sections", { params: { limit: 1 } })
      .then((r) => { if (alive) setTotal(r.data?.total_public_records || 0); })
      .catch(() => {});
    api.get("/vendors")
      .then((r) => { if (alive) setVendors(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const vendorById = useMemo(() => {
    const m = {};
    for (const v of vendors) m[v.id] = v;
    return m;
  }, [vendors]);

  // Client-side sort on the current page slice.
  const sortedDocs = useMemo(() => {
    const list = docs.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (sortBy === "save_count") {
        av = a.save_count || 0; bv = b.save_count || 0;
      } else {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return list;
  }, [docs, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const hasFilters = !!(q || classFilter || seedFilter || hasListingOnly || missingParentageOnly);
  const clearAll = () => {
    setQ(""); setClassFilter(""); setSeedFilter("");
    setHasListingOnly(false); setMissingParentageOnly(false);
  };

  return (
    <div data-testid="lineage-list-view">
      {/* Filter toolbar */}
      <div className="border border-[var(--border)] bg-[var(--surface)] p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex items-center flex-1 min-w-[260px]">
            <input type="text"
                   placeholder="Search strains, parents, aliases, tags…"
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   className="input-field !py-2 !pl-9 !pr-8 text-sm w-full"
                   data-testid="lineage-list-search" />
            <Search className="w-4 h-4 absolute left-3" style={{ color: "var(--text-muted)" }} />
            {q && (
              <button onClick={() => setQ("")}
                      className="absolute right-2.5 inline-flex items-center justify-center w-5 h-5"
                      style={{ color: "var(--text-muted)" }}
                      data-testid="lineage-list-search-clear"
                      aria-label="Clear search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
            <label className="font-mono uppercase tracking-[0.18em] text-[10px]">Rows</label>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="input-field !py-1 text-xs" data-testid="lineage-list-page-size">
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] mr-1" style={{ color: "var(--text-muted)" }}>Class</span>
          {CLASS_CHIPS.map((c) => (
            <button key={c.id || "all"}
                    onClick={() => setClassFilter(classFilter === c.id ? "" : c.id)}
                    data-testid={`lineage-list-class-${c.id || "all"}`}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors"
                    style={{
                      borderColor: classFilter === c.id ? "var(--gold)" : "var(--border)",
                      color:       classFilter === c.id ? "var(--gold)" : "var(--text-muted)",
                      background:  classFilter === c.id ? "rgba(212,175,55,0.06)" : "transparent",
                    }}>
              {c.label}
            </button>
          ))}

          <span className="text-[10px] font-mono uppercase tracking-[0.2em] mx-1 ml-3" style={{ color: "var(--text-muted)" }}>Seed</span>
          {[
            { id: "feminized",  label: "Fem" },
            { id: "regular",    label: "Reg" },
            { id: "autoflower", label: "Auto" },
          ].map((s) => (
            <button key={s.id}
                    onClick={() => setSeedFilter(seedFilter === s.id ? "" : s.id)}
                    data-testid={`lineage-list-seed-${s.id}`}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors"
                    style={{
                      borderColor: seedFilter === s.id ? "var(--gold)" : "var(--border)",
                      color:       seedFilter === s.id ? "var(--gold)" : "var(--text-muted)",
                      background:  seedFilter === s.id ? "rgba(212,175,55,0.06)" : "transparent",
                    }}>
              {s.label}
            </button>
          ))}

          <span className="text-[10px] font-mono uppercase tracking-[0.2em] mx-1 ml-3" style={{ color: "var(--text-muted)" }}>Flags</span>
          <button onClick={() => setHasListingOnly((v) => !v)}
                  data-testid="lineage-list-flag-has-listing"
                  className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors inline-flex items-center gap-1"
                  style={{
                    borderColor: hasListingOnly ? "var(--gold)" : "var(--border)",
                    color:       hasListingOnly ? "var(--gold)" : "var(--text-muted)",
                    background:  hasListingOnly ? "rgba(212,175,55,0.06)" : "transparent",
                  }}>
            <Package className="w-3 h-3" /> On CannaForge
          </button>
          <button onClick={() => setMissingParentageOnly((v) => !v)}
                  data-testid="lineage-list-flag-missing-parents"
                  className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors inline-flex items-center gap-1"
                  style={{
                    borderColor: missingParentageOnly ? "var(--gold)" : "var(--border)",
                    color:       missingParentageOnly ? "var(--gold)" : "var(--text-muted)",
                    background:  missingParentageOnly ? "rgba(212,175,55,0.06)" : "transparent",
                  }}>
            <Mountain className="w-3 h-3" /> Missing parents
          </button>

          {hasFilters && (
            <button onClick={clearAll}
                    data-testid="lineage-list-clear-filters"
                    className="ml-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--secondary)] transition-colors">
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Result strip */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 text-[11px] font-mono uppercase tracking-[0.16em]"
           style={{ color: "var(--text-muted)" }}>
        <span>
          Showing <strong style={{ color: "var(--text)" }}>{sortedDocs.length}</strong>
          {hasFilters ? " filtered" : ""} record{sortedDocs.length === 1 ? "" : "s"}
          {!hasFilters && total > 0 && (
            <> · <strong style={{ color: "var(--text-2)" }}>{total}</strong> total public</>
          )}
        </span>
        <span>Page <strong style={{ color: "var(--text)" }}>{page + 1}</strong></span>
      </div>

      {/* Table */}
      <div className="border border-[var(--border)] overflow-auto bg-[var(--surface)]">
        <table className="w-full text-xs" data-testid="lineage-list-table">
          <thead className="bg-[var(--surface-2)] sticky top-0 z-10">
            <tr className="text-left" style={{ color: "var(--text-muted)" }}>
              <ColHeader id="name"                label="Strain"        sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} test="col-name"/>
              <ColHeader id="parent_one"          label="Parent A"      sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} test="col-parent-a"/>
              <th className="p-2 font-mono uppercase tracking-[0.16em] text-[10px]">Parent B</th>
              <ColHeader id="classification"      label="Class"         sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} test="col-class"/>
              <th className="p-2 font-mono uppercase tracking-[0.16em] text-[10px]">Seed / Cycle</th>
              <th className="p-2 font-mono uppercase tracking-[0.16em] text-[10px]">Terpenes</th>
              <th className="p-2 font-mono uppercase tracking-[0.16em] text-[10px]">Breeder</th>
              <ColHeader id="verification_status" label="Status"        sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} test="col-status"/>
              <ColHeader id="save_count"          label="Saves"         sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} test="col-saves"/>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="9" className="p-8 text-center" style={{ color: "var(--text-muted)" }}>Loading…</td></tr>
            )}
            {!loading && sortedDocs.length === 0 && (
              <tr><td colSpan="9" className="p-8 text-center" style={{ color: "var(--text-muted)" }}>
                No strains match the current filters.
                {hasFilters && (
                  <button onClick={clearAll} className="ml-2 underline" style={{ color: "var(--gold)" }}>
                    Clear filters
                  </button>
                )}
              </td></tr>
            )}
            {!loading && sortedDocs.map((d) => {
              const originator = d.originator_breeder_id ? vendorById[d.originator_breeder_id] : null;
              const hasListing = (d.related_listing_ids?.length || 0) > 0;
              const v = VERIFICATION_GLYPH[d.verification_status] || VERIFICATION_GLYPH.community_submitted;
              const VIcon = v.icon;
              return (
                <tr key={d.id}
                    className="border-t border-[var(--border)] hover:bg-[var(--bg-2)] cursor-pointer transition-colors"
                    onClick={() => nav(`/strains/${d.slug || d.id}`)}
                    data-testid={`lineage-list-row-${d.slug || d.id}`}>
                  <td className="p-2.5 align-top" style={{ color: "var(--text)" }}>
                    <Link to={`/strains/${d.slug || d.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold hover:underline">
                      {d.strain_name}
                    </Link>
                    {hasListing && (
                      <span className="ml-1.5 inline-flex items-center px-1 py-0 text-[8px] font-mono uppercase tracking-[0.15em] border align-middle"
                            style={{ color: "var(--gold)", borderColor: "rgba(212,175,55,0.5)" }}
                            title="On CannaForge">
                        LIVE
                      </span>
                    )}
                  </td>
                  <td className="p-2.5 align-top" style={{ color: "var(--text-2)" }}>{d.parent_one || <em style={{ color: "var(--text-muted)" }}>—</em>}</td>
                  <td className="p-2.5 align-top" style={{ color: "var(--text-2)" }}>{d.parent_two || <em style={{ color: "var(--text-muted)" }}>—</em>}</td>
                  <td className="p-2.5 align-top font-mono text-[10px]" style={{ color: "var(--text-2)" }}>
                    {d.classification || <em style={{ color: "var(--text-muted)" }}>—</em>}
                  </td>
                  <td className="p-2.5 align-top font-mono text-[10px]" style={{ color: "var(--text-2)" }}>
                    {(d.seed_type || "—")}{d.cycle_type ? ` · ${d.cycle_type}` : ""}
                  </td>
                  <td className="p-2.5 align-top" data-testid={`lineage-list-terpenes-${d.slug || d.id}`}>
                    {Array.isArray(d.terpenes) && d.terpenes.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {d.terpenes.slice(0, 3).map((t) => (
                          <span key={t}
                                className="px-1.5 py-0 text-[9px] font-mono uppercase tracking-[0.12em] border"
                                style={{ color: "var(--gold)", borderColor: "var(--border)", borderRadius: 2 }}>
                            {t}
                          </span>
                        ))}
                        {d.terpenes.length > 3 && (
                          <span className="text-[9px] font-mono" style={{ color: "var(--text-muted)" }}>
                            +{d.terpenes.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <em style={{ color: "var(--text-muted)" }}>—</em>
                    )}
                  </td>
                  <td className="p-2.5 align-top" style={{ color: "var(--text-2)" }}>
                    {originator ? (
                      <Link to={breederUrl(originator)}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline inline-flex items-center gap-1">
                        {originator.store_name}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    ) : (d.originator_text || <em style={{ color: "var(--text-muted)" }}>—</em>)}
                  </td>
                  <td className="p-2.5 align-top">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em]"
                          style={{ color: v.color }}
                          title={v.label}>
                      <VIcon className="w-3 h-3" />
                      {d.verification_status?.replace("_", " ") || "—"}
                    </span>
                  </td>
                  <td className="p-2.5 align-top font-mono text-[11px]" style={{ color: "var(--text-2)" }}>
                    {d.save_count || 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <button onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="lineage-list-prev"
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] border border-[var(--border)] disabled:opacity-30 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
          ← Prev
        </button>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
          Page {page + 1}{docs.length < pageSize && page === 0 ? " of 1" : ""}
        </span>
        <button onClick={() => setPage((p) => p + 1)}
                disabled={docs.length < pageSize}
                data-testid="lineage-list-next"
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] border border-[var(--border)] disabled:opacity-30 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
          Next →
        </button>
      </div>
    </div>
  );
}

function ColHeader({ id, label, sortBy, sortDir, toggle, test }) {
  const active = sortBy === id;
  return (
    <th onClick={() => toggle(id)}
        data-testid={`lineage-list-${test}`}
        className="p-2 font-mono uppercase tracking-[0.16em] text-[10px] cursor-pointer select-none"
        style={{ color: active ? "var(--gold)" : "var(--text-muted)" }}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}
